import React from 'react';
import { useNavigate } from 'react-router-dom';
import SyllabusDownloadButton from '../SyllabusDownloadButton/SyllabusDownloadButton';
import styles from './SpecialityView.module.css';

function SpecialityView({ speciality }) {
  const navigate = useNavigate();

  const { mandatory, elective } = speciality.curriculumStructure || {};

  return (
    <>
      {/* Banner */}
      <div className={styles.banner}>
        {speciality.bannerImageS3 ? (
          <img src={speciality.bannerImageS3} alt={speciality.name} className={styles.bannerImg} />
        ) : (
          <div className={styles.bannerPlaceholder} />
        )}
        <div className={styles.bannerOverlay}>
          <div className={styles.bannerLeft}>
            <h1 className={styles.bannerTitle}>{speciality.name}</h1>
            {speciality.generalInfo && (
              <div className={styles.heroGrid}>
                {speciality.generalInfo.duration && (
                  <div className={styles.heroItem}>
                    <span className={styles.heroLabel}>Duración</span>
                    <span className={styles.heroValue}>{speciality.generalInfo.duration}</span>
                  </div>
                )}
                {speciality.generalInfo.schedule && (
                  <div className={styles.heroItem}>
                    <span className={styles.heroLabel}>Horarios</span>
                    <span className={styles.heroValue}>{speciality.generalInfo.schedule}</span>
                  </div>
                )}
                {speciality.generalInfo.subjects && (
                  <div className={styles.heroItem}>
                    <span className={styles.heroLabel}>Materias</span>
                    <span className={styles.heroValue}>{speciality.generalInfo.subjects}</span>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className={styles.bannerRight}>
            <button
              type="button"
              className={styles.bannerCta}
              onClick={() => navigate(`/inscripcion?careerId=${speciality._id}`)}
            >
              Inscríbete ahora
            </button>
          </div>
        </div>
      </div>

      {/* Two-column content */}
      <div className={styles.content}>

        {/* ── Left column ── */}
        <div className={styles.leftCol}>
          {speciality.tagline && <p className={styles.tagline}>{speciality.tagline}</p>}
          {speciality.description && <p className={styles.description}>{speciality.description}</p>}
          {speciality.admissionProfile && (
            <p className={styles.admissionProfile}>{speciality.admissionProfile}</p>
          )}

          {/* ¿Qué vas a aprender? | Costos — 2 columnas */}
          <div className={styles.learnCareerGrid}>
            {/* Columna izquierda */}
            {speciality.whatYouWillLearn?.length > 0 && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>¿Qué vas a aprender?</h2>
                <div className={styles.learnGrid}>
                  {speciality.whatYouWillLearn.map((block, i) => (
                    <div key={i} className={styles.learnBlock}>
                      <h3 className={styles.learnCategory}>{block.category}</h3>
                      <ul className={styles.subjectList}>
                        {block.subjects.map((s, j) => (
                          <li key={j}>{s}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {speciality.syllabusUrl && (
              <div className={styles.syllabusAside}>
                <SyllabusDownloadButton url={speciality.syllabusUrl} />
              </div>
            )}

          </div>

          {/* Estructura curricular */}
          {(mandatory?.groups?.length > 0 || elective?.groups?.length > 0) && (
            <div className={styles.curriculumWrapper}>
              {/* Obligatorias */}
              {mandatory?.groups?.length > 0 && (
                <div className={styles.curriculumBlock}>
                  <p className={styles.curriculumHeaderMandatory}>
                    {mandatory.label || 'Asignaturas Obligatorias'}
                  </p>
                  <div className={styles.curriculumBlockContent}>
                    {mandatory.groups.map((group, i) => (
                      <div key={i}>
                        <p className={styles.curriculumGroupName}>{group.groupName}</p>
                        <ul className={styles.curriculumSubjectList}>
                          {group.subjects.map((s, j) => <li key={j}>{s}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Opcionales */}
              {elective?.groups?.length > 0 && (
                <div className={styles.curriculumBlock}>
                  <p className={styles.curriculumHeaderElective}>
                    {elective.label || 'Asignaturas Opcionales'}
                    {elective.note && <span className={styles.curriculumElectiveNote}>{elective.note}</span>}
                  </p>
                  <div className={styles.curriculumElectiveGroups}>
                    {elective.groups.map((group, i) => (
                      <div key={i} className={styles.curriculumElectiveGroup}>
                        <p className={styles.curriculumGroupName}>{group.groupName}</p>
                        <ul className={styles.curriculumSubjectList}>
                          {group.subjects.map((s, j) => <li key={j}>{s}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Perfil del egresado */}
          {speciality.graduateProfile?.length > 0 && (
            <section className={styles.profileCapsule}>
              <div className={styles.profileLeft}>
                <h2 className={styles.sectionTitle}>Perfil del egresado</h2>
                <ul className={styles.bulletList}>
                  {speciality.graduateProfile.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
              <img src="/egresado.png" alt="" className={styles.degreeImg01} aria-hidden="true" />
            </section>
          )}

        </div>

        {/* ── Right column ── */}
        <div className={styles.rightCol}>
          {/* Info card */}
          {speciality.generalInfo && (
            <div className={styles.infoCard}>
              <div className={styles.infoRow}>
                <span className={styles.infoIcon}>📅</span>
                <div>
                  <p className={styles.infoLabel}>Duración</p>
                  <p className={styles.infoValue}>{speciality.generalInfo.duration}</p>
                </div>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoIcon}>🕐</span>
                <div>
                  <p className={styles.infoLabel}>Horarios</p>
                  <p className={styles.infoValue}>{speciality.generalInfo.schedule}</p>
                </div>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoIcon}>📚</span>
                <div>
                  <p className={styles.infoLabel}>Materias</p>
                  <p className={styles.infoValue}>{speciality.generalInfo.subjects}</p>
                </div>
              </div>
              {speciality.generalInfo.graduation?.length > 0 && (
                <div className={styles.infoRow}>
                  <span className={styles.infoIcon}>🎓</span>
                  <div>
                    <p className={styles.infoLabel}>Titulación</p>
                    <ol className={styles.graduationList}>
                      {speciality.generalInfo.graduation.map((g, i) => (
                        <li key={i}>{g}</li>
                      ))}
                    </ol>
                  </div>
                </div>
              )}
              <button
                type="button"
                className={styles.ctaBtn}
                onClick={() => navigate(`/inscripcion?careerId=${speciality._id}`)}
              >
                Comenzar tu inscripción ahora
              </button>
            </div>
          )}

        </div>
      </div>
    </>
  );
}

export default SpecialityView;
